/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Avatar, Box, Tab, Tabs, Typography } from '@mui/material'
import theme from 'src/theme'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import {
  fetchHydratedSchedule,
  selectHydratedSchedule,
  selectHydratedScheduleStatus,
  selectedScheduleChanged,
} from './live.slice'
import { LiveClassIcon } from '../core/icons'

export default () => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let scheduleStatus = useAppSelector(selectHydratedScheduleStatus)
  let schedule = useAppSelector(selectHydratedSchedule)

  let [weekTab, setWeekTab] = useState<string>('this')

  useEffect(() => {
    if (scheduleStatus === 'uninitialized') {
      dispatch(fetchHydratedSchedule())
    }
  }, [scheduleStatus])

  let today = dayjs()
  let nextWeekExists = Object.values(schedule[2]?.sessions || []).some((s) =>
    s?.some((session) => {
      let sessionTime = dayjs(session.sessionTime)
      return (
        sessionTime.diff(today, 'day') - (sessionTime.day() - today.day()) === 7
      )
    }),
  )

  let thisSchedule = {
    last: schedule[nextWeekExists ? 0 : 1],
    this: schedule[nextWeekExists ? 1 : 2],
    next: schedule[2],
  }[weekTab]

  let weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

  return (
    <>
      <Box sx={{ p: 2 }}>
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <LiveClassIcon />
          </Avatar>
          <Typography
            variant="h4"
            className="pl-2"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            Live Class Schedule
          </Typography>
        </div>
        <Typography paragraph textAlign="center" color="text.secondary">
          View and navigate to upcoming live classes
        </Typography>
        <Tabs
          variant="scrollable"
          value={weekTab}
          onChange={(e, num) => {
            setWeekTab(num)
            dispatch(selectedScheduleChanged(num))
          }}
        >
          <Tab value="last" label="Last Week" />
          <Tab value="this" label="This Week" />
          <Tab disabled={!nextWeekExists} value="next" label="Next Week" />
        </Tabs>
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 1,
          }}
        >
          {weekdays.map((day) => (
            <Box
              sx={{
                border: `1px solid ${theme.palette.text.secondary}`,
                borderRadius: 1,
                p: 1,
              }}
            >
              <Typography textAlign="center" variant="h6">
                {day}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!thisSchedule?.sessions[day] ? (
                  <Typography
                    color="text.secondary"
                    sx={{
                      borderTop: `1px solid ${theme.palette.text.secondary}`,
                      mt: '10px',
                      p: 0.5,
                    }}
                  >
                    There are no classes scheduled for this date.
                  </Typography>
                ) : (
                  thisSchedule?.sessions[day].map((liveClass) => (
                    <Box key={liveClass.sessionID}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <Typography
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 14,
                          }}
                        >
                          {dayjs(liveClass.sessionTime)
                            .utc(true)
                            .local()
                            .format('H:mm')}
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: `1px solid ${theme.palette.text.secondary}`,
                            width: '100%',
                          }}
                        ></Box>
                      </Box>
                      <Box
                        sx={{
                          borderRadius: 1,
                          bgcolor: theme.palette.primary.dark,
                          color: theme.palette.text.primary,
                        }}
                      >
                        <Typography sx={{ mx: 1, py: '1px' }}>
                          {liveClass.className}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          ))}
        </Box>
        {weekdays.map((day) => (
          <>
            <Typography
              variant="h5"
              sx={{
                mb: 1,
                mt: 3,
                borderBottom: `1px solid ${theme.palette.text.secondary}`,
              }}
            >
              {day}
            </Typography>
            {!thisSchedule?.sessions[day] ? (
              <Typography color="text.secondary">
                There are no classes scheduled for this date.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, 300px)',
                  gap: 2,
                  transition: 'all 300ms ease-in-out',
                }}
              >
                {thisSchedule?.sessions[day].map((liveClass) => (
                  <Box
                    key={liveClass.className + liveClass.sessionTime}
                    sx={{ position: 'relative' }}
                  >
                    <Typography
                      variant="h6"
                      textAlign="center"
                      sx={{ mb: 0, fontWeight: 'bold' }}
                    >
                      {dayjs(liveClass.sessionTime)
                        .utc(true)
                        .local()
                        .format('H:mm')}
                    </Typography>
                    <Typography
                      textAlign="center"
                      sx={{ mb: 1, fontStyle: 'italic' }}
                    >
                      {liveClass.className}
                    </Typography>
                    <Box
                      key={liveClass.className + liveClass.sessionTime}
                      sx={{
                        borderRadius: 3,
                        height: 170,
                        transition: 'transform 300ms ease-in-out',
                        '&:hover': {
                          border: `2px solid ${theme.palette.primary.main}`,
                          transform: 'scale(1.1)',
                          zIndex: 2,
                        },
                      }}
                    >
                      <img
                        onClick={() =>
                          navigate(`/live/session/${liveClass.sessionID}`)
                        }
                        style={{
                          cursor: 'pointer',
                          width: '100%',
                          height: '100%',
                          borderRadius: '10px',
                        }}
                        src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${liveClass.classImageName}`}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ))}
      </Box>
      <Loading entities={[scheduleStatus]} />
    </>
  )
}
